import { Dialog } from '../../../../components/ui';
import CourseNameHeading, { useCourseName } from '../CourseNameHeading';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import NoData from '../../../../components/common/NoData';
import CourseOverview from './CourseOverview';
import CourseComment from './CourseComment';
import CourseRating from './CourseRating';

import { decodeData } from '../../../../utils/encodeAndEncode';
import { getVideoDetailsById } from '../../../../services/organization/courses/courseContent.service';
import { EditVideoContent } from '../../../../services/organization/courses/courseModule.service';
import { usePermissions } from '../../../../context/PermissionsContext';
import { toast } from '../../../../components/ui/notify';
import { getSignedMediaUrl } from '../../../../services/media.service';
import {
  buildBitmovinSource,
  shouldLockPlayerInteractions,
} from './adminVideoPlayer.utils';
import { buildYouTubeEmbedSrcFromPageUrl, extractYouTubeVideoId } from '../../../../utils/youtubeEmbed.utils';
import BitmovinPlayer from '../../../../components/common/BitmovinPlayer';
import { isBitmovinPlayerEnabled } from '../../../../utils/videoPlayerFeatureFlag';
import { isFeedFeatureEnabled } from '../../../../utils/featureFlags';
import {
  MERIT_POINTS_MAX,
  MERIT_POINTS_MIN,
  formatMeritPointsOnBlur,
  meritPointsValidationMessage,
  parseMeritPointsForApi,
  sanitizeMeritPointsDigits,
} from '../../../../utils/meritPoints';

const bitmovinKey = process.env.REACT_APP_BITMOVIN_PLAYER_KEY;

/** Normalize DB TIME (HH:MM:SS) or plain strings into MM:SS for the duration input. */
function normalizeDurationForEdit(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  const parts = s.split(':').map((p) => p.trim());
  if (parts.length === 3 && parts.every((p) => /^\d+$/.test(p))) {
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    const sec = parseInt(parts[2], 10) || 0;
    const totalMin = h * 60 + m;
    return `${String(totalMin).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  return s;
}

function isValidMmSsDuration(value) {
  const t = (value || '').trim();
  if (!t) return true;
  return /^\d{1,4}:\d{2}$/.test(t);
}

function VideoPlayer() {
  const courseName = useCourseName();
  const { courseId, moduleId, contentId } = useParams();
  const decodedCourseId = decodeData(courseId);
  const decodedModuleId = decodeData(moduleId);
  const decodedContentId = decodeData(contentId);
  const { permissions } = usePermissions();
  const isYouTubeSourceEnabled = isFeedFeatureEnabled();

  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const loadSequenceRef = useRef(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [key, setKey] = useState(0);
  const [videoData, setVideoData] = useState(null);
  const isYouTubeVideo = videoData?.upload_view_type === 'youtube' || Boolean(extractYouTubeVideoId(videoData?.video_url));
  const [signedVideoUrl, setSignedVideoUrl] = useState(null);
  const [playerInitialized, setPlayerInitialized] = useState(false);
  const [sourceLoading, setSourceLoading] = useState(true);
  const [useBitmovin, setUseBitmovin] = useState(() => isBitmovinPlayerEnabled());

  useEffect(() => {
    const refreshPlayerFlag = () => setUseBitmovin(isBitmovinPlayerEnabled());
    window.addEventListener('storage', refreshPlayerFlag);
    return () => window.removeEventListener('storage', refreshPlayerFlag);
  }, []);

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editVideoSourceType, setEditVideoSourceType] = useState('upload'); // 'upload' | 's3' | 'youtube'
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    points: '0',
    videoUrl: '',
    videoDuration: '',
    newVideoFile: null,
    newVideoPreview: null
  });

  // Upload progress modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('uploading'); // 'uploading', 'success', 'error'
  const [uploadProgress, setUploadProgress] = useState(0);

  // Add validation errors state
  const [errors, setErrors] = useState({
    title: '',
    description: '',
    points: '',
    videoFile: '',
    videoUrl: '',
    videoDuration: ''
  });

  // Fetch video details
  useEffect(() => {
    const fetchVideoDetails = async () => {
      try {
        const response = await getVideoDetailsById({
          video_id: decodedContentId,
          module_id: decodedModuleId
        });

        if (response.success) {
          setVideoData(response.data);
          // Determine source type from upload_view_type
          let srcType = 'upload';
          if (response.data.upload_view_type === 'src') srcType = 's3';
          if (response.data.upload_view_type === 'youtube') {
            srcType = isYouTubeSourceEnabled ? 'youtube' : 's3';
          }
          setEditVideoSourceType(srcType);
          // Pre-populate edit form with current data
          const rawPts =
            response.data.earning_points !== undefined && response.data.earning_points !== null
              ? String(response.data.earning_points)
              : response.data.earnPoint !== undefined && response.data.earnPoint !== null
                ? String(response.data.earnPoint)
                : '0';
          setEditForm({
            title: response.data.video_title || response.data.video_name || '',
            description: response.data.video_description || '',
            points: formatMeritPointsOnBlur(rawPts),
            videoUrl: response.data.video_url || '',
            videoDuration: normalizeDurationForEdit(response.data.video_duration),
            newVideoFile: null,
            newVideoPreview: null
          });
        }
      } catch (error) {
        console.error('Error fetching video details:', error);
      }
    };

    if (decodedContentId && decodedModuleId) {
      fetchVideoDetails();
    }
  }, [decodedContentId, decodedModuleId, isYouTubeSourceEnabled]);

  useEffect(() => {
    let active = true;
    setSignedVideoUrl(null);
    setSourceLoading(true);

    const resolveSignedUrl = async () => {
      // For YouTube/src type, use the stored URL directly — no proxy needed
      if (isYouTubeVideo || videoData?.upload_view_type === 'src') {
        if (active) setSignedVideoUrl(videoData?.video_url || null);
        return;
      }

      if (videoData?.media_id) {
        const signedUrl = await getSignedMediaUrl({
          type: 'video',
          mediaId: videoData.media_id
        });
        if (active) setSignedVideoUrl(signedUrl || null);
        return;
      }
      if (active) setSignedVideoUrl(videoData?.video_url || null);
    };

    resolveSignedUrl();
    return () => {
      active = false;
    };
  }, [videoData, isYouTubeVideo]);

    // Reset when content changes
  useEffect(() => {
    setActiveTab('overview');
    setKey(prevKey => prevKey + 1);
    setSignedVideoUrl(null);
    setSourceLoading(true);
    loadSequenceRef.current += 1;
  }, [decodedContentId]);

  // Initialize Bitmovin player (only for non-YouTube — avoids mounting player before type is known)
  useEffect(() => {
    if (!videoData || isYouTubeVideo || !useBitmovin) {
      setPlayerInitialized(false);
      return undefined;
    }

    let script = null;
    let link = null;

    const initPlayer = () => {
      if (!window.bitmovin?.player || !playerContainerRef.current) {
        console.error('Bitmovin player not loaded or container not available');
        return;
      }

      const playerConfig = {
        key: bitmovinKey,
        playback: {
          autoplay: false,
          muted: false
        },
        ui: {
          playbackSpeedSelectionEnabled: true,
          pictureInPictureConfig: {
            enabled: true
          },
          enableFullscreen: true,
          keyboard: {
            fullscreen: true
          }
        },
        style: {
          width: '100%',
          aspectratio: '16:9',
          controls: true
        },
        adaptation: {
          preload: true
        }
      };

      try {
        const container = playerContainerRef.current;
        if (playerRef.current) {
          playerRef.current.destroy();
        }

        const player = new window.bitmovin.player.Player(container, playerConfig);
        playerRef.current = player;
        setPlayerInitialized(true);
        setSourceLoading(true);

        // Hide watermark after player is ready
        player.on('ready', () => {
          setTimeout(() => {
            const watermark = document.querySelector('.bmpui-ui-watermark');
            if (watermark) {
              watermark.style.display = 'none';
            }
          }, 100);
        });

        player.on('error', (error) => {
          console.error('Player error:', error);
        });

        player.on('fullscreenenter', () => {
          const playerContainer = playerContainerRef.current;
          if (playerContainer) {
            playerContainer.style.maxWidth = '100vw';
            playerContainer.style.maxHeight = '100vh';
          }
        });

        player.on('fullscreenexit', () => {
          const playerContainer = playerContainerRef.current;
          if (playerContainer) {
            playerContainer.style.maxWidth = '';
            playerContainer.style.maxHeight = '';
          }
        });

      } catch (error) {
        console.error('Error initializing player:', error);
      }
    };

    // Load Bitmovin scripts
    if (!document.querySelector('script[src*="bitmovinplayer.js"]')) {
      script = document.createElement('script');
      script.src = 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer.js';
      script.async = true;
      script.onload = initPlayer;
      document.body.appendChild(script);
    } else {
      initPlayer();
    }

    if (!document.querySelector('link[href*="bitmovinplayer-ui.css"]')) {
      link = document.createElement('link');
      link.href = 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      setPlayerInitialized(false);
    };
  }, [videoData?.upload_view_type, videoData?.id, decodedContentId, isYouTubeVideo, useBitmovin]);

  // Load video when both player is initialized and videoData is available
  useEffect(() => {
    if (isYouTubeVideo || !useBitmovin) {
      setSourceLoading(false);
      return undefined;
    }

    const player = playerRef.current;
    const source = buildBitmovinSource(signedVideoUrl, videoData);

    if (!playerInitialized || !player || !source) {
      return undefined;
    }

    const currentLoadSequence = ++loadSequenceRef.current;
    let active = true;

    setSourceLoading(true);

    const loadSource = async () => {
      try {
        if (typeof player.unload === 'function') {
          try {
            await player.unload();
          } catch (_error) {
            // Ignore unload failures while replacing the current source.
          }
        }

        await player.load(source);

        if (!active || currentLoadSequence !== loadSequenceRef.current) {
          return;
        }

        setSourceLoading(false);
      } catch (error) {
        if (!active || currentLoadSequence !== loadSequenceRef.current) {
          return;
        }

        setSourceLoading(false);
        console.error('Error loading video source:', {
          url: signedVideoUrl,
          error,
        });
      }
    };

    loadSource();

    return () => {
      active = false;
    };
  }, [playerInitialized, signedVideoUrl, videoData, isYouTubeVideo, useBitmovin]);

  const isPlayerLocked = useBitmovin && shouldLockPlayerInteractions({
      playerInitialized,
      signedVideoUrl,
      sourceLoading,
    });

  const HandleEditVideo = () => {
    if (!permissions.course_module_content_management_edit) {
      toast.warning("You don't have permission to edit video content");
      return;
    }
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    let srcType = 'upload';
    if (videoData?.upload_view_type === 'src') srcType = 's3';
    if (videoData?.upload_view_type === 'youtube') {
      srcType = isYouTubeSourceEnabled ? 'youtube' : 's3';
    }
    setEditVideoSourceType(srcType);
    // Reset form to current video data
    const rawPts =
      videoData?.earning_points !== undefined && videoData?.earning_points !== null
        ? String(videoData.earning_points)
        : videoData?.earnPoint !== undefined && videoData?.earnPoint !== null
          ? String(videoData.earnPoint)
          : '0';
    setEditForm({
      title: videoData?.video_title || videoData?.video_name || '',
      description: videoData?.video_description || '',
      points: formatMeritPointsOnBlur(rawPts),
      videoUrl: videoData?.video_url || '',
      videoDuration: normalizeDurationForEdit(videoData?.video_duration),
      newVideoFile: null,
      newVideoPreview: null
    });
    setErrors({
      title: '',
      description: '',
      points: '',
      videoFile: '',
      videoUrl: '',
      videoDuration: ''
    });
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      title: '',
      description: '',
      points: '',
      videoFile: '',
      videoUrl: '',
      videoDuration: ''
    };

    // Title validation
    if (!editForm.title.trim()) {
      newErrors.title = 'Video title is required';
      isValid = false;
    } else if (editForm.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
      isValid = false;
    }

    // Description validation
    if (!editForm.description.trim()) {
      newErrors.description = 'Video description is required';
      isValid = false;
    } else if (editForm.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
      isValid = false;
    }

    const mp = meritPointsValidationMessage(editForm.points);
    if (mp) {
      newErrors.points = mp;
      isValid = false;
    }

    // Video file validation
    if (editForm.newVideoFile) {
      const maxSize = 150 * 1024 * 1024; // 150MB
      if (editForm.newVideoFile.size > maxSize) {
        newErrors.videoFile = 'File size must be less than 150MB';
        isValid = false;
      }
      if (!editForm.newVideoFile.type.startsWith('video/')) {
        newErrors.videoFile = 'Please upload a valid video file';
        isValid = false;
      }
    }

    const urlTrim = (editForm.videoUrl || '').trim();
    if (editVideoSourceType === 's3') {
      if (!urlTrim) {
        newErrors.videoUrl = 'S3 / direct video URL is required';
        isValid = false;
      }
    } else if (editVideoSourceType === 'youtube') {
      if (!urlTrim) {
        newErrors.videoUrl = 'YouTube URL is required';
        isValid = false;
      } else if (!extractYouTubeVideoId(urlTrim)) {
        newErrors.videoUrl = 'Enter a valid YouTube watch, youtu.be, or embed link';
        isValid = false;
      }
    }

    const durTrim = (editForm.videoDuration || '').trim();
    if (durTrim && !isValidMmSsDuration(durTrim)) {
      newErrors.videoDuration = 'Use MM:SS (e.g., 05:30)';
      isValid = false;
    }
    if ((editVideoSourceType === 's3' || editVideoSourceType === 'youtube') && !durTrim) {
      newErrors.videoDuration = 'Video duration is required for URL-based videos';
      isValid = false;
    }

    if (editVideoSourceType === 'upload' && editForm.newVideoFile && !durTrim) {
      newErrors.videoDuration = 'Enter duration in MM:SS (e.g., 05:30) if it was not detected from the file';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;

    // Check character limits
    if (name === 'title' && value.length > 100) return;
    if (name === 'description' && value.length > 500) return;

    const next = name === 'points' ? sanitizeMeritPointsDigits(value) : value;

    setEditForm((prev) => ({
      ...prev,
      [name]: next,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clear previous error
      setErrors(prev => ({
        ...prev,
        videoFile: ''
      }));

      // Validate file type
      if (!file.type.startsWith('video/')) {
        setErrors(prev => ({
          ...prev,
          videoFile: 'Please select a valid video file'
        }));
        e.target.value = '';
        return;
      }

      // Validate file size (150MB limit)
      const maxSize = 150 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors(prev => ({
          ...prev,
          videoFile: 'File size must be less than 150MB'
        }));
        e.target.value = '';
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setEditForm((prev) => {
        if (prev.newVideoPreview) {
          URL.revokeObjectURL(prev.newVideoPreview);
        }
        return {
          ...prev,
          newVideoFile: file,
          newVideoPreview: previewUrl,
        };
      });

      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const minutes = Math.floor(video.duration / 60);
        const seconds = Math.floor(video.duration % 60);
        const formattedDuration = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        setEditForm((prev) => ({
          ...prev,
          videoDuration: formattedDuration,
        }));
        setErrors((prev) => ({ ...prev, videoDuration: '' }));
      };
      video.src = previewUrl;
    }
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Show upload modal
      setShowUploadModal(true);
      setUploadStatus('uploading');
      setUploadProgress(0);

      const formData = new FormData();
      formData.append("video_id", decodedContentId);
      formData.append("video_title", editForm.title);
      formData.append("module_id", decodedModuleId);
      formData.append("description", editForm.description);
      formData.append('earning_points', String(parseMeritPointsForApi(editForm.points)));
      const durationPayload = (editForm.videoDuration || '').trim() || normalizeDurationForEdit(videoData?.video_duration) || '';
      formData.append('duration', durationPayload);

      const uploadViewType =
        editVideoSourceType === 'youtube'
          ? 'youtube'
          : editVideoSourceType === 's3'
            ? 'src'
            : 'uploaded';
      formData.append("upload_view_type", uploadViewType);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 1000);

      if (editVideoSourceType === 's3' || editVideoSourceType === 'youtube') {
        formData.append("src", editForm.videoUrl || '');
        formData.append("video_changed", "false");
        formData.append("video_removed", "false");
      } else {
        // Add flags to indicate video status
        const videoChanged = editForm.newVideoFile !== null;
        formData.append("video_changed", videoChanged ? "true" : "false");
        formData.append("video_removed", "false");

        // Only append video file if it has changed
        if (videoChanged && editForm.newVideoFile) {
          formData.append("upload_file", editForm.newVideoFile);
        }
      }

      // Debug logging
      for (let pair of formData.entries()) {
        if (pair[1] instanceof File) {
        } else {
        }
      }

      const response = await EditVideoContent(formData);

      // Clear the progress interval
      clearInterval(progressInterval);

      if (response.success) {
        // Set upload status to success
        setUploadStatus('success');
        setUploadProgress(100);

        // Refresh video data
        const videoResponse = await getVideoDetailsById({
          video_id: decodedContentId,
          module_id: decodedModuleId
        });
        if (videoResponse.success) {
          setVideoData(videoResponse.data);
        }

        // Close the modal after a short delay
        setTimeout(() => {
          setShowUploadModal(false);
          setShowEditModal(false);
          let srcType = 'upload';
          if (videoResponse.data?.upload_view_type === 'src') srcType = 's3';
          if (videoResponse.data?.upload_view_type === 'youtube') {
            srcType = isYouTubeSourceEnabled ? 'youtube' : 's3';
          }
          setEditVideoSourceType(srcType);
          // Reset form
          const savedRaw =
            videoResponse.data?.earning_points !== undefined && videoResponse.data?.earning_points !== null
              ? String(videoResponse.data.earning_points)
              : videoResponse.data?.earnPoint !== undefined && videoResponse.data?.earnPoint !== null
                ? String(videoResponse.data.earnPoint)
                : '0';
          setEditForm({
            title: videoResponse.data?.video_title || videoResponse.data?.video_name || '',
            description: videoResponse.data?.video_description || '',
            points: formatMeritPointsOnBlur(savedRaw),
            videoUrl: videoResponse.data?.video_url || '',
            videoDuration: normalizeDurationForEdit(videoResponse.data?.video_duration),
            newVideoFile: null,
            newVideoPreview: null
          });
        }, 2000);
      } else {
        setUploadStatus('error');
        setTimeout(() => {
          setShowUploadModal(false);
        }, 2000);
      }
    } catch (error) {
      setUploadStatus('error');
      setTimeout(() => {
        setShowUploadModal(false);
      }, 2000);
    }
  };

  return (
    <>
      <div className="tw-w-full tw-max-w-[1200px] tw-m-[0_auto] tw-p-[20px] tw-bg-[#fff] tw-rounded-[12px] tw-shadow-[0_2px_10px_rgba(0,_0,_0,_0.05)] max-[768px]:tw-p-[15px] video-section-wrapper">
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-6"}>
          <div className="tw-min-w-[0px] ui-column tw-w-full tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-center">
            <h4 className="tw-mb-0">{videoData?.video_title || videoData?.video_name || 'Video Player'}</h4>
            {permissions.course_module_content_management_edit && (
              <button
                className={"tw-bg-[#3152e8] tw-text-[white] tw-border-0 tw-p-[10px_25px] tw-rounded-[8px] tw-font-medium [transition:all_0.3s_ease] tw-flex tw-items-center tw-gap-[8px] [&:hover]:tw-bg-[#2440c9] [&:hover]:[transform:translateY(-1px)] edit-video-btn"}
                onClick={HandleEditVideo}
                disabled={!permissions.course_module_content_management_edit}
                title={!permissions.course_module_content_management_edit ? "You don't have permission to edit video" : "Edit Video"}
              >
                <Icon icon="fluent:edit-24-regular" />
                Edit Video
              </button>
            )}
          </div>
        </div>

        <CourseNameHeading name={courseName} />

          <div className="tw-w-full tw-bg-[#000] tw-rounded-[12px] tw-overflow-hidden tw-shadow-[0_4px_20px_rgba(0,_0,_0,_0.1)] tw-max-w-[1000px] tw-m-[0_auto_30px] max-[768px]:tw-rounded-[8px] max-[768px]:tw-mb-[20px] max-[768px]:tw-mt-[20px] video-player-container">
            <div className={"[&:fullscreen]:tw-w-[100vw] [&:fullscreen]:tw-h-[100vh] [&:fullscreen]:tw-pb-0 [&:fullscreen]:tw-flex [&:fullscreen]:tw-flex-col [&:fullscreen]:tw-justify-center [&:fullscreen]:tw-bg-[#000] [&:fullscreen]:tw-z-[9999] [&:fullscreen_>_div:first-child]:tw-w-full [&:fullscreen_.fullscreen-activity-overlay]:tw-absolute [&:fullscreen_.fullscreen-activity-overlay]:tw-inset-0 [&:fullscreen_.fullscreen-activity-overlay]:tw-z-[99998] [&:fullscreen_.fullscreen-activity-overlay]:tw-border-0 [&:fullscreen_.fullscreen-activity-overlay]:tw-p-0 [&:fullscreen_.fullscreen-activity-overlay]:tw-bg-[transparent] [&:fullscreen_.fullscreen-activity-overlay]:tw-cursor-default [&:fullscreen_.video-restricted-controls]:tw-absolute [&:fullscreen_.video-restricted-controls]:tw-right-[24px] [&:fullscreen_.video-restricted-controls]:tw-bottom-0 [&:fullscreen_.video-restricted-controls]:tw-left-[24px] [&:fullscreen_.video-restricted-controls]:tw-w-auto [&:fullscreen_.video-restricted-controls]:tw-z-[99999] [&:fullscreen_.video-restricted-controls]:tw-visible [&:fullscreen_.video-restricted-controls]:tw-opacity-[0] [&:fullscreen_.video-restricted-controls]:tw-pointer-events-none [&:fullscreen_.video-restricted-controls]:[transform:translateY(8px)] [&:fullscreen_.video-restricted-controls]:[transition:opacity_180ms_ease,_transform_180ms_ease] [&:fullscreen.fullscreen-controls-visible_.video-restricted-controls]:tw-opacity-[1] [&:fullscreen.fullscreen-controls-visible_.video-restricted-controls]:tw-pointer-events-auto [&:fullscreen.fullscreen-controls-visible_.video-restricted-controls]:[transform:translateY(0)] [&:has(.video-restricted-controls)]:tw-h-auto [&:has(.video-restricted-controls)]:tw-pb-0 tw-relative tw-w-full tw-h-0 tw-pb-[56.25%] tw-bg-[#000] player-wrapper"}>
              {!videoData ? (
                <div className="tw-flex tw-justify-center tw-items-center tw-rounded-xl tw-bg-slate-900" style={{ minHeight: '240px' }}>
                  <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600" role="status">
                    <span className="tw-sr-only">Loading...</span>
                  </div>
                </div>
              ) : isYouTubeVideo ? (
                <div className={"tw-relative [aspect-ratio:16_/_9] tw-w-full [&_>_:is(iframe,_video,_div)]:tw-absolute [&_>_:is(iframe,_video,_div)]:tw-inset-0 [&_>_:is(iframe,_video,_div)]:tw-h-full [&_>_:is(iframe,_video,_div)]:tw-w-full ui-video-frame"}>
                  <iframe
                    key={`yt-${decodedContentId}-${videoData.video_url || ''}`}
                    src={buildYouTubeEmbedSrcFromPageUrl(videoData.video_url)}
                    title={videoData.video_title || 'YouTube Video'}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                </div>
              ) : useBitmovin ? (
                <>
                  <div
                    ref={playerContainerRef}
                    className={`!tw-absolute tw-top-0 tw-left-0 !tw-w-full !tw-h-full [&_video]:!tw-w-full [&_video]:!tw-h-full [&_video]:!tw-object-contain bitmovin-player${isPlayerLocked ? "   tw-pointer-events-none bitmovin-player--locked" : ""}`}
                  ></div>
                  {isPlayerLocked && (
                    <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center [background:rgba(0,_0,_0,_0.35)] tw-z-[3] bitmovin-player-loading">
                      <div className="tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-slate-50" role="status">
                        <span className="tw-sr-only">Loading video...</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <video
                  key={`native-${signedVideoUrl || decodedContentId}`}
                  controls
                  preload="metadata"
                  src={signedVideoUrl || undefined}
                  onLoadedMetadata={() => setSourceLoading(false)}
                  onError={() => setSourceLoading(false)}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    background: '#000',
                  }}
                />
              )}
            </div>
          </div>

        <div className="max-[768px]:tw-overflow-visible tw-bg-[#fff] tw-rounded-[12px] tw-overflow-hidden tw-mt-[30px] video-tabs">
          <div className="tw-border-b-[1px] tw-border-solid tw-border-b-[#e0e0e0] tw-bg-[#f8f9fa] tab-header">
            <ul className={"max-[767.98px]:[&[aria-label=\"breadcrumb\"]]:!tw-p-[0.75rem_1rem] max-[767.98px]:[&[aria-label=\"breadcrumb\"]_h4]:tw-text-[1.1rem] tw-flex tw-gap-1 [list-style:none] [&_.ui-nav-item]:tw-m-0 [&_.ui-nav-link:hover]:tw-text-[#3152e8] [&_.ui-nav-link:hover]:tw-border-0 [&_.ui-nav-link.active::after]:[content:''] [&_.ui-nav-link.active::after]:tw-absolute [&_.ui-nav-link.active::after]:tw-bottom-0 [&_.ui-nav-link.active::after]:tw-left-0 [&_.ui-nav-link.active::after]:tw-w-full [&_.ui-nav-link.active::after]:tw-h-[2px] [&_.ui-nav-link.active::after]:tw-bg-[#3152e8] max-[768px]:tw-p-[0_10px] max-[768px]:[&_.ui-nav-link]:tw-p-[12px_15px] max-[768px]:[&_.ui-nav-link]:tw-text-[14px] [&_.ui-nav-link.disabled]:!tw-cursor-not-allowed [&_.ui-nav-link.disabled]:![pointer-events:all] [&_.ui-nav-link.disabled]:tw-opacity-[0.6] [&_button.disabled]:!tw-cursor-not-allowed [&_button.disabled]:![pointer-events:all] [&_button.disabled]:tw-opacity-[0.6] [&_button[disabled]]:!tw-cursor-not-allowed [&_button[disabled]]:![pointer-events:all] [&_button[disabled]]:tw-opacity-[0.6] [&_.ui-nav-link.active]:tw-text-[#3152e8] [&_.ui-nav-link.active]:tw-border-0 [&_.ui-nav-link.active]:tw-rounded-br-[0px] [&_.ui-nav-link.active]:tw-rounded-bl-[0px] [&_.ui-nav-link.active]:tw-border-b-[2px] [&_.ui-nav-link.active]:[--tw-border-opacity:1] [&_.ui-nav-link.active]:tw-border-[rgb(64_84_214_/_var(--tw-border-opacity,_1))] [&_.ui-nav-link.active]:tw-bg-[transparent] tw-border-0 tw-p-[0_20px] tw-mb-5 tw-border-b-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] tw-overflow-x-auto tw-flex-nowrap [scrollbar-width:thin] [&_.ui-nav-link]:tw-border-0 [&_.ui-nav-link]:tw-text-[#6c757d] [&_.ui-nav-link]:tw-p-[15px_25px] [&_.ui-nav-link]:tw-font-medium [&_.ui-nav-link]:[transition:all_0.3s_ease] [&_.ui-nav-link]:tw-relative [&_.ui-nav-link]:tw-whitespace-nowrap ui-nav ui-nav-tabs"} role="tablist">
              <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                <button
                  className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link   ${activeTab === "overview" ? "active" : ""}`}
                  onClick={() => setActiveTab('overview')}
                  role="tab"
                >
                  <Icon icon="fluent:info-24-regular" className="tw-me-2" />
                  Overview
                </button>
              </li>
              <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                <button
                  className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link   ${activeTab === "comments" ? "active" : ""}`}
                  onClick={() => setActiveTab('comments')}
                  role="tab"
                >
                  <Icon icon="fluent:comment-24-regular" className="tw-me-2" />
                  Comments
                </button>
              </li>
              <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                <button
                  className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link   ${activeTab === "ratings" ? "active" : ""}`}
                  onClick={() => setActiveTab('ratings')}
                  role="tab"
                >
                  <Icon icon="fluent:star-24-regular" className="tw-me-2" />
                  Ratings
                </button>
              </li>
            </ul>
          </div>

          <div className={"max-[768px]:tw-p-[2px] tw-p-[15px] tw-bg-[#fff] [&_>_.tab-pane]:tw-hidden [&_>_.tab-pane.active]:tw-block tab-content"}>
            {activeTab === 'overview' && (
              <div className="tab-pane fade show active tw-p-0" role="tabpanel">
                <CourseOverview key={`overview-${key}`} videoData={videoData} />
              </div>
            )}

            {activeTab === 'comments' && (
              <div className="tab-pane fade show active tw-p-0" role="tabpanel">
                <CourseComment
                  contentData={{
                    id: decodedContentId,
                    module_id: decodedModuleId
                  }}
                  player={playerRef.current}
                  mentorId={videoData?.mentor_id}
                />
              </div>
            )}

            {activeTab === 'ratings' && (
              <div className="tab-pane fade show active tw-p-0" role="tabpanel">
                <CourseRating
                  key={`ratings-${key}`}
                  contentData={{
                    id: decodedContentId,
                    course_id: decodedCourseId
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Video Modal */}
      {showEditModal && (
        <Dialog show onHide={handleCloseEditModal} size="lg">
              <Dialog.Header closeButton>
                <Dialog.Title >Edit Video</Dialog.Title>

              </Dialog.Header>
              <Dialog.Body >
                {/* Video Title */}
                <div className="tw-mb-4">
                  <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Video Title *</label>
                  <input
                    type="text"
                    className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input   ${errors.title ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                    name="title"
                    value={editForm.title}
                    onChange={handleEditFormChange}
                    placeholder="Enter video title"
                    maxLength={100}
                  />
                  <div className="tw-flex tw-justify-between tw-mt-1">
                    <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">
                      Maximum 100 characters allowed ({100 - editForm.title.length} remaining)
                    </small>
                    {errors.title && (
                      <small className="!tw-text-[var(--danger-color)] tw-text-rose-600">{errors.title}</small>
                    )}
                  </div>
                </div>

                {/* Video Description */}
                <div className="tw-mb-4">
                  <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Video Description *</label>
                  <textarea
                    className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input   ${errors.description ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                    name="description"
                    value={editForm.description}
                    onChange={handleEditFormChange}
                    placeholder="Enter video description"
                    rows="3"
                    maxLength={500}
                  />
                  <div className="tw-flex tw-justify-between tw-mt-1">
                    <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">
                      Maximum 500 characters allowed ({500 - editForm.description.length} remaining)
                    </small>
                    {errors.description && (
                      <small className="!tw-text-[var(--danger-color)] tw-text-rose-600">{errors.description}</small>
                    )}
                  </div>
                </div>

                {/* Merit points */}
                <div className="tw-mb-4">
                  <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">
                    Merit points{' '}
                    <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-font-normal">
                      (min {MERIT_POINTS_MIN}, max {MERIT_POINTS_MAX})
                    </span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input   ${errors.points ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                    name="points"
                    value={editForm.points}
                    onChange={handleEditFormChange}
                    onBlur={(e) => {
                      setEditForm((prev) => ({
                        ...prev,
                        points: formatMeritPointsOnBlur(e.target.value),
                      }));
                    }}
                    placeholder={`${MERIT_POINTS_MIN}–${MERIT_POINTS_MAX}`}
                    aria-label={`Merit points, ${MERIT_POINTS_MIN} to ${MERIT_POINTS_MAX}`}
                  />
                  <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">
                    Whole number from {MERIT_POINTS_MIN} to {MERIT_POINTS_MAX}. Awarded when the trainee completes this
                    video.
                  </small>
                  {errors.points && (
                    <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.points}</div>
                  )}
                </div>

                {/* Video duration (editable; upload + new file auto-fills from file metadata) */}
                <div className="tw-mb-4">
                  <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Video duration</label>
                  <input
                    type="text"
                    className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input   ${errors.videoDuration ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                    name="videoDuration"
                    value={editForm.videoDuration}
                    onChange={handleEditFormChange}
                    placeholder={
                      editVideoSourceType === 'upload' && editForm.newVideoFile
                        ? 'Filled from uploaded file (edit if needed)'
                        : 'e.g., 05:30 (MM:SS)'
                    }
                    autoComplete="off"
                  />
                  <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-block">
                    {editVideoSourceType === 'upload'
                      ? 'Required when keeping the existing file if the duration is wrong or missing. Choosing a new video sets duration automatically (you can adjust).'
                      : 'Required for direct URL. Use MM:SS (e.g., 10:05).'}
                  </small>
                  {errors.videoDuration && (
                    <div className="tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback tw-block">{errors.videoDuration}</div>
                  )}
                </div>

                {/* Video Source Type Selection */}
                <div className="tw-mb-4">
                  <label className="![font-weight:var(--font-weight-semibold)] tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label tw-font-semibold">Video Source *</label>
                  <div className="tw-flex tw-gap-2 tw-flex-wrap">
                    <div
                      className={`[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-flex-1 tw-p-4 tw-border tw-rounded-xl tw-text-center   ${editVideoSourceType === "upload" ? "tw-border-brand-600" : "tw-border-slate-500"}`}
                      style={{ cursor: 'pointer', minWidth: '90px', backgroundColor: editVideoSourceType === 'upload' ? 'rgba(var(--ui-primary-rgb),0.08)' : '' }}
                      onClick={() => setEditVideoSourceType('upload')}
                    >
                      <Icon icon="fluent:cloud-arrow-up-24-regular" width="24" height="24" className={editVideoSourceType === "upload" ? "!tw-text-[var(--primary-color)] tw-text-brand-600" : "!tw-text-[var(--secondary-color)] tw-text-slate-500"} />
                      <div className={`!tw-text-[12px] tw-text-sm tw-mt-1   ${editVideoSourceType === "upload" ? "![font-weight:var(--font-weight-semibold)] !tw-text-[var(--primary-color)] tw-text-brand-600 tw-font-semibold" : "!tw-text-[var(--secondary-color)] tw-text-slate-500"}`}>Upload Video</div>
                    </div>
                    <div
                      className={`[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-flex-1 tw-p-4 tw-border tw-rounded-xl tw-text-center   ${editVideoSourceType === "s3" ? "tw-border-brand-600" : "tw-border-slate-500"}`}
                      style={{ cursor: 'pointer', minWidth: '90px', backgroundColor: editVideoSourceType === 's3' ? 'rgba(var(--ui-primary-rgb),0.08)' : '' }}
                      onClick={() => setEditVideoSourceType('s3')}
                    >
                      <Icon icon="fluent:link-24-regular" width="24" height="24" className={editVideoSourceType === "s3" ? "!tw-text-[var(--primary-color)] tw-text-brand-600" : "!tw-text-[var(--secondary-color)] tw-text-slate-500"} />
                      <div className={`!tw-text-[12px] tw-text-sm tw-mt-1   ${editVideoSourceType === "s3" ? "![font-weight:var(--font-weight-semibold)] !tw-text-[var(--primary-color)] tw-text-brand-600 tw-font-semibold" : "!tw-text-[var(--secondary-color)] tw-text-slate-500"}`}>S3 / Direct URL</div>
                    </div>
                    {isYouTubeSourceEnabled && (
                      <div
                        className={`[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-flex-1 tw-p-4 tw-border tw-rounded-xl tw-text-center   ${editVideoSourceType === "youtube" ? "tw-border-brand-600" : "tw-border-slate-500"}`}
                        style={{ cursor: 'pointer', minWidth: '90px', backgroundColor: editVideoSourceType === 'youtube' ? 'rgba(var(--ui-primary-rgb),0.08)' : '' }}
                        onClick={() => setEditVideoSourceType('youtube')}
                      >
                        <Icon icon="mdi:youtube" width="24" height="24" className={editVideoSourceType === "youtube" ? "!tw-text-[var(--danger-color)] tw-text-rose-600" : "!tw-text-[var(--secondary-color)] tw-text-slate-500"} />
                        <div className={`!tw-text-[12px] tw-text-sm tw-mt-1   ${editVideoSourceType === "youtube" ? "![font-weight:var(--font-weight-semibold)] !tw-text-[var(--primary-color)] tw-text-brand-600 tw-font-semibold" : "!tw-text-[var(--secondary-color)] tw-text-slate-500"}`}>YouTube URL</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Current media preview for URL-based sources */}
                {editVideoSourceType === 's3' && (
                  <div className="tw-mb-4">
                    <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Current video</label>
                    <div className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-border tw-rounded-xl tw-p-4 tw-bg-slate-50"}>
                      {signedVideoUrl ? (
                        <div className={"tw-relative [aspect-ratio:16_/_9] tw-w-full [&_>_:is(iframe,_video,_div)]:tw-absolute [&_>_:is(iframe,_video,_div)]:tw-inset-0 [&_>_:is(iframe,_video,_div)]:tw-h-full [&_>_:is(iframe,_video,_div)]:tw-w-full ui-video-frame tw-overflow-hidden tw-rounded-xl tw-bg-black"}>
                          <BitmovinPlayer
                            src={signedVideoUrl}
                            title={videoData?.video_title || videoData?.video_name || 'Current video preview'}
                            objectFit="contain"
                            style={{ width: '100%', height: '100%' }}
                          />
                        </div>
                      ) : (
                        <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm tw-mb-0">
                          Save a direct video URL below. Preview appears when the URL is playable.
                        </p>
                      )}
                      <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-mt-2 tw-text-sm tw-break-words tw-text-slate-500">
                        <span className="![font-weight:var(--font-weight-semibold)] !tw-text-[var(--secondary-color)] tw-font-semibold tw-text-slate-500">URL on file: </span>
                        {videoData?.video_url ? (
                          <a href={videoData.video_url} target="_blank" rel="noopener noreferrer">
                            {videoData.video_url}
                          </a>
                        ) : (
                          '—'
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* URL input for YouTube */}
                {isYouTubeSourceEnabled && editVideoSourceType === 'youtube' && (
                  <div className="tw-mb-4">
                    <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">YouTube URL *</label>
                    <input
                      type="url"
                      className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:tw-ring-[rgb(224_231_255)] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] tw-border-solid tw-border-[rgb(226_232_240)] tw-px-3 tw-py-2.5 tw-text-[rgb(15_23_42)] tw-shadow-sm tw-min-h-[42px] ${errors.videoUrl ? "!tw-border-[#dc3545] [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                      value={editForm.videoUrl}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEditForm(prev => ({ ...prev, videoUrl: value }));
                        if (errors.videoUrl) {
                          setErrors(prev => ({ ...prev, videoUrl: '' }));
                        }
                      }}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                    {errors.videoUrl && (
                      <div className="tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14)] tw-leading-[1rem] ui-field-feedback tw-block">{errors.videoUrl}</div>
                    )}
                  </div>
                )}

                {/* URL input for S3 / direct URL */}
                {editVideoSourceType === 's3' && (
                  <div className="tw-mb-4">
                    <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">S3 / Direct Video URL *</label>
                    <input
                      type="url"
                      className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input   ${errors.videoUrl ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                      value={editForm.videoUrl}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEditForm(prev => ({ ...prev, videoUrl: value }));
                        if (errors.videoUrl) {
                          setErrors(prev => ({ ...prev, videoUrl: '' }));
                        }
                      }}
                      placeholder="https://your-bucket.s3.amazonaws.com/video.mp4"
                    />
                    {errors.videoUrl && (
                      <div className="tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback tw-block">{errors.videoUrl}</div>
                    )}
                  </div>
                )}

                {/* File upload for direct upload */}
                {editVideoSourceType === 'upload' && (
                  <div className="tw-mb-4">
                    <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Replace video file</label>
                    <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm">
                      Leave empty to keep the current upload. Choose a file only if you want to replace it.
                    </p>
                    <div className={`[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-border tw-rounded-xl tw-p-6 tw-text-center   ${errors.videoFile ? "tw-border-rose-600" : ""}`}>
                      <Icon icon="fluent:video-add-24-regular" width="48" height="48" className="!tw-text-[var(--primary-color)] tw-text-brand-600 tw-mb-2" />
                      <p className="!tw-text-[var(--primary-color)] tw-mb-2 tw-text-brand-600">Upload New Video</p>
                      <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm tw-mb-4">Maximum file size: 150MB</p>
                      <input
                        type="file"
                        className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input   ${errors.videoFile ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                        accept="video/*"
                        onChange={handleVideoFileChange}
                      />
                      {errors.videoFile && (
                        <div className="!tw-text-[var(--danger-color)] !tw-text-[12px] tw-text-rose-600 tw-text-sm tw-mt-2">{errors.videoFile}</div>
                      )}
                      {editForm.newVideoPreview && (
                        <div className="tw-mt-4">
                          <p className="!tw-text-[var(--success-color)] !tw-text-[12px] tw-text-emerald-600 tw-text-sm">New video selected: {editForm.newVideoFile?.name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Current Video Preview */}
                {editVideoSourceType === 'upload' && (
                  <div className="tw-mb-4">
                    <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Current uploaded video</label>
                    <div className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-border tw-rounded-xl tw-p-4"}>
                      {editForm.newVideoPreview ? (
                        <div>
                          <div className={"tw-relative [aspect-ratio:16_/_9] tw-w-full [&_>_:is(iframe,_video,_div)]:tw-absolute [&_>_:is(iframe,_video,_div)]:tw-inset-0 [&_>_:is(iframe,_video,_div)]:tw-h-full [&_>_:is(iframe,_video,_div)]:tw-w-full ui-video-frame tw-overflow-hidden tw-rounded-xl tw-bg-black"}>
                            <BitmovinPlayer
                              src={editForm.newVideoPreview}
                              title={editForm.newVideoFile?.name || 'New uploaded video'}
                              objectFit="contain"
                              style={{ width: '100%', height: '100%' }}
                            />
                          </div>
                          <div className="tw-mt-2">
                            <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-block">
                              New file: {editForm.newVideoFile?.name || 'Untitled'}
                            </small>
                            <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-block">
                              Size: {(editForm.newVideoFile?.size / (1024 * 1024)).toFixed(2)} MB
                            </small>
                            <div className="tw-mt-2">
                              <span className="tw-inline-flex tw-w-fit tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-solid tw-border-sky-200 tw-bg-sky-50 tw-px-2.5 tw-py-1 !tw-text-[12px] tw-font-medium tw-leading-4 tw-text-sky-700">
                                <Icon icon="fluent:checkmark-circle-20-filled" width="14" height="14" />
                                New video selected
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : signedVideoUrl ? (
                        <div>
                          <div className={"tw-relative [aspect-ratio:16_/_9] tw-w-full [&_>_:is(iframe,_video,_div)]:tw-absolute [&_>_:is(iframe,_video,_div)]:tw-inset-0 [&_>_:is(iframe,_video,_div)]:tw-h-full [&_>_:is(iframe,_video,_div)]:tw-w-full ui-video-frame tw-overflow-hidden tw-rounded-xl tw-bg-black"}>
                            <BitmovinPlayer
                              src={signedVideoUrl}
                              title={videoData?.video_title || videoData?.video_name || 'Current uploaded video'}
                              objectFit="contain"
                              style={{ width: '100%', height: '100%' }}
                            />
                          </div>
                          <div className="tw-mt-2">
                            <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-block">
                              Stored duration: {videoData.video_duration || '—'} (edit above if needed)
                            </small>
                            <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-block tw-break-words">
                              File / path:{' '}
                              {videoData.video_url
                                ? videoData.video_url.split('/').pop()?.split('?')[0] || videoData.video_url
                                : videoData.video_name || videoData.video_title || '—'}
                            </small>
                          </div>
                        </div>
                      ) : (
                        <NoData message="No video available" icon="fluent:video-24-regular" />
                      )}
                    </div>
                  </div>
                )}
              </Dialog.Body>
              <Dialog.Footer >
                <button
                  type="button"
                  className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-secondary"}
                  onClick={handleCloseEditModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}
                  onClick={handleSaveChanges}
                >
                  Save Changes
                </button>
              </Dialog.Footer>
            </Dialog>
      )}

      {/* Upload Progress Modal */}
      {showUploadModal && (
        <Dialog show backdrop="static" keyboard={false}><Dialog.Title className="tw-sr-only">Video upload progress</Dialog.Title>
              <Dialog.Body className="tw-text-center tw-p-6">
                {uploadStatus === 'uploading' && (
                  <>
                    <div className="tw-mb-4">
                      <Icon icon="fluent:cloud-arrow-up-24-regular" width="48" height="48" className="!tw-text-[var(--primary-color)] tw-text-brand-600" />
                    </div>
                    <h5 className="tw-mb-4">Uploading Video...</h5>
                    <div className="tw-bg-[rgb(241_245_249_/_var(--tw-bg-opacity,_1))] tw-rounded-full tw-overflow-hidden tw-flex tw-h-2 [--tw-bg-opacity:1] ui-progress tw-mb-4" style={{ height: '20px' }}>
                      <div
                        className="tw-h-full tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] tw-rounded-full [transition:width_0.3s_ease] tw-flex tw-items-center tw-justify-center [--tw-bg-opacity:1] tw-text-[0.75rem] tw-leading-[1rem] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [transition-property:width] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:300ms] ui-progress-bar ui-progress-bar-striped ui-progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                        aria-valuenow={uploadProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {uploadProgress}%
                      </div>
                    </div>
                    <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">Please wait while we process your video...</p>
                  </>
                )}

                {uploadStatus === 'success' && (
                  <>
                    <div className="tw-mb-4">
                      <Icon icon="fluent:checkmark-circle-24-filled" width="48" height="48" className="!tw-text-[var(--success-color)] tw-text-emerald-600" />
                    </div>
                    <h5 className="!tw-text-[var(--success-color)] tw-text-emerald-600 tw-mb-4">Upload Successful!</h5>
                    <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">Your video has been updated successfully.</p>
                  </>
                )}

                {uploadStatus === 'error' && (
                  <>
                    <div className="tw-mb-4">
                      <Icon icon="fluent:error-circle-24-filled" width="48" height="48" className="!tw-text-[var(--danger-color)] tw-text-rose-600" />
                    </div>
                    <h5 className="tw-text-slate-900 tw-mb-4">Your video hasn’t finished uploading</h5>
                    <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">We couldn’t finish uploading your video. Please try again.</p>
                  </>
                )}
              </Dialog.Body>
            </Dialog>
      )}
    </>
  );
}

export default VideoPlayer;
